import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Input, Select, Tag, Spin, Empty, Space, Pagination } from 'antd';
import { Link, useSearchParams } from 'react-router-dom';
import { CalendarOutlined, UserOutlined, SearchOutlined, LockOutlined } from '@ant-design/icons';
import { fetchPortalPosts } from '../../services/portalApi';
import '../../components/portal/portal.css';

const { Title, Text, Paragraph } = Typography;

const PostCard = ({ post, onTagClick }) => (
  <div className="portal-masonry-item">
    <Link to={`/posts/${post.slug}`} className="portal-post-card" style={{ display: 'block', color: 'inherit', textDecoration: 'none' }}>
      {post.featuredMediaUrl && (
        <img
          src={post.featuredMediaUrl}
          alt=""
          style={{ width: '100%', display: 'block', objectFit: 'cover' }}
        />
      )}
      <div style={{ padding: 20 }}>
        {post.categoryName && (
          <Text type="secondary" style={{ display: 'block', fontSize: 12, letterSpacing: 0.4, marginBottom: 6 }}>
            {post.categoryName}
          </Text>
        )}
        {post.needsPassword && (
          <Tag icon={<LockOutlined />} style={{ marginBottom: 8 }}>Password</Tag>
        )}
        <Title level={4} style={{ margin: '0 0 8px' }}>
          {post.title}
        </Title>
        {post.excerpt && (
          <Paragraph type="secondary" ellipsis={{ rows: 3 }} style={{ marginBottom: 12 }}>
            {post.excerpt}
          </Paragraph>
        )}
        <Space size={16} wrap>
          {post.publishedAt && (
            <Text type="secondary" style={{ fontSize: 13 }}>
              <CalendarOutlined style={{ marginRight: 4 }} />
              {new Date(post.publishedAt).toLocaleDateString()}
            </Text>
          )}
          {post.authorName && (
            <Text type="secondary" style={{ fontSize: 13 }}>
              <UserOutlined style={{ marginRight: 4 }} />
              {post.authorName}
            </Text>
          )}
        </Space>
        {post.tagNames?.length > 0 && (
          <div style={{ marginTop: 12 }}>
            {post.tagNames.map((t) => (
              <Tag
                key={t}
                style={{ cursor: 'pointer', marginBottom: 4 }}
                onClick={(e) => { e.preventDefault(); onTagClick(t); }}
              >
                {t}
              </Tag>
            ))}
          </div>
        )}
      </div>
    </Link>
  </div>
);

const PortalPostsScreen = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ posts: [], totalCount: 0, currentPage: 1 });
  const [loading, setLoading] = useState(true);
  const [titleQuery, setTitleQuery] = useState(searchParams.get('q') || '');

  const page = Number(searchParams.get('page') || 1);
  const category = searchParams.get('category') || undefined;
  const tag = searchParams.get('tag') || undefined;
  const q = searchParams.get('q') || undefined;

  const load = useCallback(() => {
    setLoading(true);
    fetchPortalPosts({ page, limit: 12, category, tag, query: q })
      .then(setData)
      .finally(() => setLoading(false));
  }, [page, category, tag, q]);

  useEffect(() => {
    load();
  }, [load]);

  const onTagClick = (t) => setSearchParams({ tag: t, page: '1' });

  return (
    <div style={{ paddingTop: 32 }}>
      <Title style={{ marginBottom: 4 }}>Research updates</Title>
      <Text type="secondary">News, findings, and platform updates from the OpenEP|NET community.</Text>
      <div style={{ margin: '24px 0' }}>
        <Input.Search
          allowClear
          size="large"
          placeholder="Search by title"
          style={{ width: '100%', marginBottom: 12 }}
          value={titleQuery}
          onChange={(e) => setTitleQuery(e.target.value)}
          enterButton={<SearchOutlined />}
          onSearch={(value) => {
            const next = { page: '1' };
            if (value) next.q = value;
            if (category) next.category = category;
            if (tag) next.tag = tag;
            setSearchParams(next);
          }}
        />
        <Select
          allowClear
          size="large"
          placeholder="Category"
          style={{ width: '100%' }}
          value={category}
          onChange={(value) => {
            const next = { page: '1' };
            if (value) next.category = value;
            if (q) next.q = q;
            setSearchParams(next);
          }}
          options={[
            ...new Set((data.posts || []).map((p) => p.categoryName).filter(Boolean)),
          ].map((name) => ({ value: name, label: name }))}
        />
      </div>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      ) : data.posts?.length ? (
        <>
          <div className="portal-masonry">
            {data.posts.map((post) => (
              <PostCard key={post.id} post={post} onTagClick={onTagClick} />
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24, marginBottom: 32 }}>
            <Pagination
              current={data.currentPage || page}
              pageSize={12}
              total={data.totalCount}
              onChange={(p) => {
                const next = { page: String(p) };
                if (category) next.category = category;
                if (tag) next.tag = tag;
                if (q) next.q = q;
                setSearchParams(next);
              }}
            />
          </div>
        </>
      ) : (
        <Empty description="No published posts" style={{ padding: 48 }} />
      )}
    </div>
  );
};

export default PortalPostsScreen;
